
// src/components/InvoiceDetails.js
import React, { useState, useEffect } from "react"; 
import httpClient from "../../../api/httpClient"; import { Search, Download } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { addTab, removeTab, setActiveTab } from "../../../redux/slices/invoiceSlice";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import MyInvoicesTab from "./TabContent/MyinvoiceTab";
import ChargesAuditTab from "./TabContent/ChargesAudit";
import EstimatedChargesTab from "./TabContent/EstimatedChargesDetails";
import TemplatesInvoicesTab from "./TabContent/TemplatesTab";
import DraftsInvoicesTab from "./TabContent/DraftsTab";
import NewCarrierInvoiceTab from "./TabContent/NewCarrierInvoice";
import ViewInvoiceTab from "./TabContent/EditCarrierInvoiceTab";
import RejectedInvoicesTab from "./TabContent/RejectedInvoicesTab";

import { incrementAuditRefresh } from '../../../redux/slices/invoiceSlice.js';
import Cookies from "js-cookie";
import { getRole, PERMS } from "../../../../src/features/ACS-Dashboard-Modules/Invoice/Roles.js";
import { usePrivilege } from "../../../guard/usePrivilege";

const InvoiceDetails = () => {
  const officeCode = Cookies.get("officeCodes")||"DIAMOND-META";
 const role = getRole(officeCode);
 const perms = PERMS[role];
  const dispatch = useDispatch();
  const auditRefreshKey    = useSelector(s => s.invoices.auditRefreshKey);

  const defaultTabs = useSelector(state => state.invoices.defaultTabs);
  const dynamicTabs = useSelector(state => state.invoices.dynamicTabs);
  const activeTab = useSelector(state => state.invoices.activeTab);

  const tabs = [...defaultTabs, ...dynamicTabs];
  const defaultTabsCount = defaultTabs.length;
  const listViewState = useSelector((s) => s.invoices.listViewState || {});
  const savedMyInvoicesFilter = listViewState["my-invoices"]?.filter;

  const { hasPrivilege } = usePrivilege();
  const canCreate       = hasPrivilege("invoices:CREATE");
  const canViewRejected = hasPrivilege("invoices:VIEW_REJECTED");

    

  const [totalInvoices, setTotalInvoices] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [statusOptions, setStatusOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState(() =>
    savedMyInvoicesFilter !== undefined ? savedMyInvoicesFilter : "Pending"
  ); 
  const [auditStatusFilter, setAuditStatusFilter] = useState("All Statuses"); 

  const currentTab = tabs[activeTab];
  const isChargesAuditTab = currentTab && currentTab.id === "charges-audit";
  const isDraftsTab = currentTab && currentTab.id === "drafts-invoices";
  const isRejectedTab = currentTab && currentTab.id === "rejected-invoices";
  const isMyInvoicesTab = currentTab && currentTab.id === "my-invoices";
  
  // ✅ Determine if footer should be shown
  const showFooter = isChargesAuditTab || isDraftsTab || isRejectedTab || isMyInvoicesTab;
  const statusApiToView = {
    // Add mappings if needed
    // "PENDING": "Pending Approval",
  };

  const statusViewToApi = Object.fromEntries(
    Object.entries(statusApiToView).map(([api, view]) => [view, api])
  );

  const [footerPaginationInfo, setFooterPaginationInfo] = useState({
    currentPage: 1,
    itemsPerPage: 50,
    totalItems: 0,
    totalPages: 0
  });

  const handlePaginationUpdate = (paginationData) => {
    setFooterPaginationInfo(paginationData);
  };

  const handleSearch = () => {
    if (!searchInput.trim()) return;

    if (isChargesAuditTab) {
      // Charges Audit has its own data model (charges, not invoices) and keeps
      // its existing in-place search rather than opening a new tab.
      setSearchQuery(searchInput);
      setRefreshKey(k => k + 1);
      return;
    }

    const baseId = isDraftsTab
      ? "drafts-invoices"
      : isRejectedTab
      ? "rejected-invoices"
      : "my-invoices";

    const baseLabel =
      baseId === "drafts-invoices"   ? "Drafts" :
      baseId === "rejected-invoices" ? "Rejected" :
      "Invoices";

    const newTabId = `search-${baseId}-${Date.now()}`;
    dispatch(addTab({
      id: newTabId,
      name: `Search(${baseLabel}): ${searchInput}`,
      contentType: "searchResults",
      baseId,
      searchQuery: searchInput,
      isDynamic: true,
      createdAt: Date.now(),
    }));
    dispatch(setActiveTab({ index: tabs.length }));
  };

  const toViewStatus = (apiStatus) => statusApiToView[apiStatus] ?? apiStatus;
  const isHiddenStatus = (apiStatus) => statusApiToView[apiStatus] === null;
  const toApiStatus = (viewStatus) => statusViewToApi[viewStatus] ?? viewStatus;

  // Auto-clear search when input is empty
useEffect(() => {
  if (searchInput === "" && searchQuery !== "") {
    setSearchQuery("");
    setRefreshKey(k => k + 1);
  }
}, [searchInput, searchQuery]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await httpClient.get(
          `/api/invoices/statuses`,
          {
            headers: { 
              Authorization: `Bearer ${Cookies.get("authToken")}`,
              "Office-Code": officeCode
            }
          }
        );
        
        if (cancelled) return;
        
        const apiStatuses = (Array.isArray(res.data) ? res.data : [])
          .map(s => String(s).trim())
          .filter(Boolean);

          
          // Map common variations to standard names
          const statusMap = {
            // "No Discrepancy": "Passed",
            // "Discrepancy": "Failed",
            // "no discrepancy": "Passed",
            // "discrepancy": "Failed",
          };
    
          const normalizedStatuses = apiStatuses.map(s => statusMap[s] || s);
    
          //const requiredAuditStatuses = ["Passed", "Failed"];
          const allStatuses = [...new Set([...normalizedStatuses])];
    
          const viewStatuses = Array.from(
            new Set(
              allStatuses
                .filter(s => !isHiddenStatus(s))
                .map(toViewStatus)
                .filter(Boolean)
            )
          ).sort();
    
              
          setStatusOptions(viewStatuses);
    
      } catch (err) {
        console.error("Failed to load invoice statuses:", err);
        setStatusOptions(["Draft", "Pending", "Approved", "Rejected"]);
      }
    })();
    
    return () => { cancelled = true; };
  }, []);

  const handleStatusFilter = (selectedViewValue) => {
    const nextApiValue = selectedViewValue === "All Statuses" 
      ? null 
      : toApiStatus(selectedViewValue);
    
    setStatusFilter(nextApiValue);
    setSearchQuery("");
    setRefreshKey(k => k + 1);
  };
  const handleAuditStatusFilter = (selectedStatus) => {
    setAuditStatusFilter(selectedStatus);
  };

  const handleEditInvoice = invoice => {
    const invoiceId = invoice.invoiceNumber || invoice.id;
    if (!invoiceId) {
      console.error("No valid invoice identifier found.");
      return;
    }
    const tabKey = `edit-${invoiceId}-${Date.now()}`;
    dispatch(addTab({ id: tabKey, name: invoiceId, type: "edit", data: invoice }));
    dispatch(setActiveTab({ index: tabs.length }));
  };



  const handleRemoveTab = index => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ tabIndex: index }));
  };

  const handleAuditChange = () => {
    dispatch(incrementAuditRefresh());
  };

  const handleCreateInvoice = () => {
   
  
    const newTabsCount = dynamicTabs.filter(t => t.type === "new").length;
    const tabName = newTabsCount === 0
      ? "New Invoice"
      : `New Invoice ${newTabsCount + 1}`;
  
    const id = `new-${Date.now()}`;
  
    dispatch(addTab({ id, name: tabName, type: "new" }));
  
    dispatch(setActiveTab({ index: tabs.length }));
  };
  


  const defaultTabContents = {
    "my-invoices": (
      <MyInvoicesTab
        key="my-invoices"
        refreshKey={refreshKey}
        onAuditChange={handleAuditChange}
        searchQuery=""
        statusFilter={statusFilter}
        onApplyStatusFilter={setStatusFilter}
        onUpdateCount={setTotalInvoices}
        onInvoiceClick={handleEditInvoice}
        onPaginationUpdate={handlePaginationUpdate}

      />
    ),
    "charges-audit": (
       <ChargesAuditTab commentMore actions
        key="charges-audit"
        refreshKey={auditRefreshKey}
        searchQuery={searchQuery}
        statusFilter={auditStatusFilter} 
        onApplyStatusFilter={setAuditStatusFilter}
        onAuditChange={handleAuditChange}
        onInvoiceClick={handleEditInvoice} 
        onPaginationUpdate={handlePaginationUpdate}

      />
    ),
    "estimated-charges": <EstimatedChargesTab key="estimated-charges"       onPaginationUpdate={handlePaginationUpdate}
    />,
    // "templates-invoices": <TemplatesInvoicesTab key="templates-invoices" templates={[]} />,
     "drafts-invoices": (
       <DraftsInvoicesTab
         key="drafts-invoices"
         searchQuery=""
         refreshKey={refreshKey}
         onPaginationUpdate={handlePaginationUpdate}
       />
     ),
     "rejected-invoices": (
      <RejectedInvoicesTab
        key="rejected-invoices"
        searchQuery=""
        refreshKey={refreshKey}
        onInvoiceClick={handleEditInvoice}
        onPaginationUpdate={handlePaginationUpdate}
      />
    ),
  };

  

  return (
    <div className="p-fluid-1 shadow-md flex flex-col flex-1 h-full min-h-0 overflow-hidden">
      {/* Top Bar */}
      <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
        <div className="flex flex-wrap items-center gap-2">
        {!isChargesAuditTab ? (
            <FilterButton
              text={statusFilter ? toViewStatus(statusFilter) : "All Statuses"}  // ✅ ADD text prop
              options={["All Statuses", ...statusOptions]}
              selectedValue={statusFilter ? toViewStatus(statusFilter) : "All Statuses"}  // ✅ ADD selectedValue
              onSelect={handleStatusFilter}  // ✅ CHANGE from onFilterChange to onSelect
              backgroundColor="white"
              textColor="black"
            />
          ) : (
            <FilterButton
              text={auditStatusFilter === "All Statuses" ? "All Statuses" : auditStatusFilter}
              options={["All Statuses", "No Discrepancy", "Discrepancy", "Resolved"]}
              selectedValue={auditStatusFilter}
              onSelect={handleAuditStatusFilter}
              backgroundColor="white"
              textColor="black"
            />
            
          )}
          
           {/* Search Box with Lucide Search Icon */}
           <SearchBox
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              placeholder="Search Invoices"
            />
                  
          <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">{totalInvoices} invoices</span>
          </div>
        

        <div className="flex items-center gap-2">
          <div className="p-2 rounded-md hover:bg-gray-100 transition cursor-pointer">
            
          </div>
          {canCreate &&(
          <Button
            text="Create Invoice"
            onClick={handleCreateInvoice}
            backgroundColor="#223688"
            textColor="white"
            fontSize="14px"
            padding="10px 18px"
            borderRadius="24px"
          />
         ) }
        

        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1  mt-fluid-2 min-h-0 flex flex-col">
        <Tabs
          tabs={tabs.map(tab => tab.name)}
          tabContents={tabs.map((tab, idx) => {
            if (tab.contentType === "searchResults") {
              if (tab.baseId === "drafts-invoices") {
                return (
                  <DraftsInvoicesTab
                    key={tab.id}
                    searchQuery={tab.searchQuery}
                    refreshKey={refreshKey}
                    stateKey={tab.id}
                    onPaginationUpdate={handlePaginationUpdate}
                    isSearchTab
                  />
                );
              }
              if (tab.baseId === "rejected-invoices") {
                return (
                  <RejectedInvoicesTab
                    key={tab.id}
                    searchQuery={tab.searchQuery}
                    refreshKey={refreshKey}
                    stateKey={tab.id}
                    onInvoiceClick={handleEditInvoice}
                    onPaginationUpdate={handlePaginationUpdate}
                    isSearchTab
                  />
                );
              }
              return (
                <MyInvoicesTab
                  key={tab.id}
                  searchQuery={tab.searchQuery}
                  statusFilter={statusFilter}
                  onApplyStatusFilter={setStatusFilter}
                  onAuditChange={handleAuditChange}
                  onUpdateCount={setTotalInvoices}
                  onInvoiceClick={handleEditInvoice}
                  onPaginationUpdate={handlePaginationUpdate}
                  stateKey={tab.id}
                  isSearchTab
                />
              );
            }

            if (defaultTabContents[tab.id]) {
              return defaultTabContents[tab.id];
            } else if (tab.type === "edit") {
              return (
                <ViewInvoiceTab
                  key={tab.id}
                  invoice={tab.data}
                  onClose={() => handleRemoveTab(idx)}
                  onSave={() => {
                    handleRemoveTab(idx);
                    setRefreshKey(k => k + 1);
                    // MyInvoicesTab only reacts to the Redux auditRefreshKey (see its
                    // useInvoiceListData call) -- it never reads the refreshKey PROP set
                    // above, so recalling/approving/rejecting from this detail view (the
                    // "edit" tab) closed the tab but left "My Invoices" showing stale data
                    // until a full page reload. Bump the Redux counter too.
                    dispatch(incrementAuditRefresh());
                  }}
                />
              );
            } else if (tab.type === "new") {
              const preselected = Array.isArray(tab.data) ? tab.data : [];
              const draftInv = !Array.isArray(tab.data) && tab.data && typeof tab.data === 'object' ? tab.data : null;


                                                        const tabIndex = defaultTabsCount + dynamicTabs.findIndex(t => t.id === tab.id);
            
              return (
                <NewCarrierInvoiceTab
                  key={tab.id}
                  title={tab.name}
                  draftInvoice={draftInv}                  
                  shipments={preselected}
                  onSave={() => {
                    // close this tab
                    dispatch(removeTab({ tabIndex }));
                    // trigger a data refresh in My Invoices
                    setRefreshKey(k => k + 1);
                  }}
                />
              );
            } else {
              return <div key={tab.id}>No Content</div>;
            }
          })}
          activeTab={activeTab}
          onTabChange={i => dispatch(setActiveTab({ index: i }))}
          isDeletable={tabs.map((_, idx) => idx >= defaultTabsCount)}
          onRemoveTab={handleRemoveTab}  // now just passes index
          keepAlive={tabs.map(
            (tab) => tab.contentType === "searchResults" || tab.id === "my-invoices"
          )}
        />
      </div>
  
    </div>
  );
};

export default InvoiceDetails;
